import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';

/**
 * Outlook OAuth connection status.
 */
export function useOutlookStatus() {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();

    // Listen for connect/disconnect events from SettingsIntegrationsPage
    // so React Query consumers see the update immediately (no 5-min stale cache).
    useEffect(() => {
        const invalidate = () => queryClient.invalidateQueries({ queryKey: ['outlook', 'status'] });
        window.addEventListener('outlook-connected', invalidate);
        window.addEventListener('outlook-disconnected', invalidate);
        return () => {
            window.removeEventListener('outlook-connected', invalidate);
            window.removeEventListener('outlook-disconnected', invalidate);
        };
    }, [queryClient]);

    return useQuery({
        queryKey: ['outlook', 'status'],
        queryFn: async () => {
            try {
                const data = await apiFetch('/oauth/outlook/status', getAuthHeader());
                return {
                    connected: data.connected,
                    needs_reconnect: data.needs_reconnect || false,
                    // #484 — `needs_reconnect` is true whenever the calendar
                    // scope is missing, even though Mail.Send still works.
                    // Sender selection must key on the mail-only signal.
                    //
                    // The `??` fallbacks matter during a rolling deploy: a
                    // frontend image can be newer than its backend. Falling
                    // back to `needs_reconnect` is the conservative reading,
                    // and defaulting `has_calendar_scope` to true means a
                    // missing field never manufactures a warning.
                    mail_needs_reconnect:
                        data.mail_needs_reconnect ?? data.needs_reconnect ?? false,
                    has_calendar_scope: data.has_calendar_scope ?? true,
                    email: data.outlook_email || '',
                };
            } catch (err) {
                const status = err?.response?.status;
                if (status === 401 || status === 404) {
                    return {
                        connected: false,
                        needs_reconnect: false,
                        mail_needs_reconnect: false,
                        has_calendar_scope: false,
                        email: '',
                    };
                }
                throw err;
            }
        },
        staleTime: 5 * 60 * 1000,
    });
}

/**
 * Outlook email detail — fetches the full email body from Graph API.
 * Used by PersonalThreadView for fetch-on-open when the list data only has
 * a snippet. The Outlook detail endpoint returns body + body_type.
 *
 * @param {string} emailId - The Graph message ID (without the 'outlook_' prefix).
 * @param {boolean} enabled - Set to false to disable the query.
 */
export function useOutlookEmailDetail(emailId, enabled = true) {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: ['outlook', 'email', emailId],
        queryFn: async () => {
            const data = await apiFetch(`/oauth/outlook/emails/${emailId}`, getAuthHeader());
            return data;
        },
        enabled: enabled && !!emailId,
        staleTime: 5 * 60 * 1000,
    });
}

/**
 * Outlook calendar-specific connection status.
 * Checks both OAuth connection and calendar scope; used by the Calendar page
 * for the dynamic Outlook badge. Polls every 5 minutes.
 * Listens for connect/disconnect events for immediate updates.
 */
export function useOutlookCalendarStatus() {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();

    // Listen for connect/disconnect events from SettingsIntegrationsPage
    // so the Outlook badge updates immediately (no 5-min stale cache).
    useEffect(() => {
        const invalidate = () => queryClient.invalidateQueries({ queryKey: ['outlook-calendar-status'] });
        window.addEventListener('outlook-connected', invalidate);
        window.addEventListener('outlook-disconnected', invalidate);
        return () => {
            window.removeEventListener('outlook-connected', invalidate);
            window.removeEventListener('outlook-disconnected', invalidate);
        };
    }, [queryClient]);

    return useQuery({
        queryKey: ['outlook-calendar-status'],
        queryFn: async () => {
            try {
                const data = await apiFetch('/oauth/outlook/status', getAuthHeader());
                return {
                    connected: data.connected && data.has_calendar_scope,
                    needs_reconnect: data.connected && (data.needs_reconnect || !data.has_calendar_scope),
                    outlook_email: data.outlook_email,
                };
            } catch (err) {
                const status = err?.response?.status;
                if (status === 401 || status === 404) {
                    return { connected: false, needs_reconnect: false, outlook_email: null };
                }
                throw err;
            }
        },
        refetchInterval: 300000,  // 5 minutes — connection status changes rarely
        staleTime: 5 * 60 * 1000,  // Don't refetch on every window focus
    });
}

